import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, IconButton, ListSubheader, MenuItem, Modal, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import RadiusButton from '../../../../components/RadiusButton';
import { useToaster } from '../../../../providers/ToasterProvider';
import { createVenue, findVenuesByName, updateVenueById } from '../../../../services/venueManagementService';
import {
    CONTACT_MAX_LENGTH,
    MAX_COUNT_LIMIT,
    VENUE_CODE_PATTERN,
    VENUE_NAME_MAX_LENGTH,
    isValidContactNumber,
    toVenueCode,
} from './venueManagementConstants';

const requiredId = z.union([z.number(), z.literal('')]).refine((value) => value !== '', { message: 'Required.' });

const venueSchema = z.object({
    venueName: z.string().trim().min(1, { message: 'Required.' }).max(VENUE_NAME_MAX_LENGTH, { message: `At most ${VENUE_NAME_MAX_LENGTH} characters.` }),
    venueCode: z.string().min(1, { message: 'Required.' })
        .regex(VENUE_CODE_PATTERN, { message: '4 to 10 letters, digits or -, not starting or ending with -.' }),
    maxCount: z.string().trim().min(1, { message: 'Required.' })
        .refine((value) => /^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= MAX_COUNT_LIMIT, { message: `Whole number from 1 to ${MAX_COUNT_LIMIT}.` }),
    serverManagerName: z.string().trim().max(VENUE_NAME_MAX_LENGTH),
    contactNumber: z.string().trim()
        .refine((value) => !value || isValidContactNumber(value), { message: 'Enter a valid phone number (7 to 15 digits).' }),
    stateId: requiredId,
    cityId: requiredId,
});

const emptyForm = { venueName: '', venueCode: '', maxCount: '', serverManagerName: '', contactNumber: '', stateId: '', cityId: '' };

const DUPLICATE_NAME_CHECK_DELAY_MS = 400;

// Add or edit one venue. venue is null when adding.
export const VenueFormModal = ({ open, venue, locations, defaultStateId, onClose, onSaved }) => {

    const { addNotification } = useToaster();
    const [isSaving, setIsSaving] = useState(false);
    const [sameNameVenues, setSameNameVenues] = useState([]);
    const editMode = Boolean(venue);

    const { control, handleSubmit, reset, watch, setValue, setError } = useForm({
        resolver: zodResolver(venueSchema),
        defaultValues: emptyForm,
    });

    useEffect(() => {
        if (!open) return;
        setSameNameVenues([]);
        reset(venue ? {
            venueName: venue.venueName,
            venueCode: venue.venueCode ?? '',
            // 0 means "not set yet" (older venues): show an empty field for the admin to fill.
            maxCount: venue.maxCount ? String(venue.maxCount) : '',
            serverManagerName: venue.serverManagerName ?? '',
            contactNumber: venue.contactNumber ?? '',
            stateId: venue.stateId ?? '',
            cityId: venue.cityId ?? '',
        } : { ...emptyForm, stateId: defaultStateId ?? '' });
    }, [open, venue, defaultStateId, reset]);

    const venueName = watch('venueName');
    const stateId = watch('stateId');

    // Venue name may repeat (the code is the unique key), so a same-name venue is a warning, not an error.
    useEffect(() => {
        const name = venueName?.trim();
        if (!open || !name) {
            setSameNameVenues([]);
            return;
        }
        // A reply for an older name (or a closed popup) must not show its warning.
        let isCurrent = true;
        const timer = setTimeout(async () => {
            try {
                const res = await findVenuesByName(name, venue?.id ?? null);
                if (isCurrent && res.code === 'OK') setSameNameVenues(res.data);
            } catch (error) {
                console.log('There is an error while checking venue name', error);
            }
        }, DUPLICATE_NAME_CHECK_DELAY_MS);
        return () => {
            isCurrent = false;
            clearTimeout(timer);
        };
    }, [open, venueName, venue]);

    // Only states that have at least one city can hold a venue; grouped under their country.
    const stateOptions = useMemo(() => {
        const options = [];
        [...locations.countries].sort((a, b) => a.name.localeCompare(b.name)).forEach((country) => {
            const states = locations.states
                .filter((state) => state.countryId === country.id && state.cityCount > 0)
                .sort((a, b) => a.name.localeCompare(b.name));
            if (!states.length) return;
            options.push(<ListSubheader key={`country-${country.id}`}>{country.name}</ListSubheader>);
            states.forEach((state) => options.push(<MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>));
        });
        return options;
    }, [locations]);

    const cityOptions = useMemo(() => locations.cities
        .filter((city) => city.stateId === stateId)
        .sort((a, b) => a.name.localeCompare(b.name)), [locations, stateId]);

    const submitForm = async (values) => {
        const body = {
            venueName: values.venueName.trim().replace(/\s+/g, ' '),
            venueCode: values.venueCode,
            maxCount: Number(values.maxCount),
            serverManagerName: values.serverManagerName.trim(),
            contactNumber: values.contactNumber.trim(),
            cityId: values.cityId,
        };
        setIsSaving(true);
        try {
            const res = editMode ? await updateVenueById(venue.id, body) : await createVenue(body);
            if (res?.code === 'OK') {
                addNotification(editMode ? 'Venue updated successfully.' : 'Venue added successfully.', 'success');
                onSaved();
            }
        } catch (error) {
            const message = error?.response?.data?.message ?? 'Venue could not be saved.';
            if (/code/i.test(message)) {
                setError('venueCode', { message }, { shouldFocus: true });
            } else {
                addNotification(message, 'danger');
            }
            console.log('There is an error while saving venue', error);
        } finally {
            setIsSaving(false);
        }
    }

    const textField = (name, label, { required = false, ...props } = {}) => (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState }) => (
                <TextField
                    {...field}
                    {...props}
                    size='small'
                    fullWidth
                    label={<>{label}{required && <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>}</>}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                />
            )}
        />
    );

    return (
        <Modal open={open} hideBackdrop={true} className='backdrop' onClose={onClose}>
            <Box className='modal-container venue-modal' sx={{ maxWidth: 480 }}>
                <Box className='venue-modal-header'>
                    <Typography variant='h6' component='h6' sx={{ color: 'primary.main' }}>
                        {editMode ? 'Edit Venue' : 'Add Venue'}
                    </Typography>
                    {editMode && venue.venueCode && <span className='venue-code-chip'>{venue.venueCode}</span>}
                    <IconButton size='small' sx={{ ml: 'auto' }} onClick={onClose} aria-label='Close'>✕</IconButton>
                </Box>

                <Box component='form' id='venue-form' autoComplete='off' noValidate onSubmit={handleSubmit(submitForm)} className='venue-modal-body'>
                    {textField('venueName', 'Venue name', { required: true, inputProps: { maxLength: VENUE_NAME_MAX_LENGTH }, autoFocus: true })}

                    {sameNameVenues.length > 0 && (
                        <Alert severity='warning' sx={{ py: 0.5 }}>
                            A venue with this name already exists. Check it is not the same venue:
                            <Box component='ul' sx={{ m: 0, mt: 0.5, pl: 2 }}>
                                {sameNameVenues.map((existing) => (
                                    <li key={existing.id}>
                                        <b>{existing.venueName}</b> · {existing.venueCode} · {existing.cityName ?? '—'}, {existing.stateName ?? '—'}
                                    </li>
                                ))}
                            </Box>
                        </Alert>
                    )}

                    <Box className='venue-form-row'>
                        <Controller
                            name='venueCode'
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    onChange={(e) => field.onChange(toVenueCode(e.target.value))}
                                    size='small'
                                    fullWidth
                                    label={<>Venue code<Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box></>}
                                    inputProps={{ className: 'venue-code-input' }}
                                    error={Boolean(fieldState.error)}
                                    helperText={fieldState.error?.message ?? '4–10 letters, digits or - (e.g. JWCC-MUM)'}
                                />
                            )}
                        />
                        {textField('maxCount', 'Max count', { required: true, type: 'number', inputProps: { min: 1, step: 1, inputMode: 'numeric' } })}
                    </Box>

                    <Box className='venue-form-row'>
                        {textField('serverManagerName', 'Server Manager Name', { inputProps: { maxLength: VENUE_NAME_MAX_LENGTH }, title: 'Server Manager Name' })}
                        {textField('contactNumber', 'Contact number', { type: 'tel', inputProps: { maxLength: CONTACT_MAX_LENGTH, inputMode: 'tel' } })}
                    </Box>

                    <Controller
                        name='stateId'
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                onChange={(e) => {
                                    field.onChange(e.target.value);
                                    setValue('cityId', '');
                                }}
                                select
                                size='small'
                                fullWidth
                                label={<>State<Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box></>}
                                error={Boolean(fieldState.error)}
                                helperText={fieldState.error?.message}
                            >
                                {stateOptions}
                            </TextField>
                        )}
                    />

                    <Controller
                        name='cityId'
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                select
                                size='small'
                                fullWidth
                                disabled={stateId === ''}
                                label={<>City<Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box></>}
                                error={Boolean(fieldState.error)}
                                helperText={fieldState.error?.message ?? (stateId === '' ? 'Select a state first' : '')}
                            >
                                {cityOptions.map((city) => <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>)}
                            </TextField>
                        )}
                    />
                </Box>

                <Box className='venue-modal-footer'>
                    <RadiusButton variant='contained' color='grey' onClick={onClose} disabled={isSaving}>Cancel</RadiusButton>
                    <LoadingButton type='submit' form='venue-form' variant='contained' loading={isSaving} sx={{ borderRadius: 40, textTransform: 'capitalize' }}>
                        {editMode ? 'Save' : 'Submit'}
                    </LoadingButton>
                </Box>
            </Box>
        </Modal>
    );
}
